import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { IInvoiceDataApiPort } from '@subscriptions/application/ports';
import type { CreateInvoiceDataDto, InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { createInvoiceData } from '@subscriptions/domain/entities';
import { mapInvoiceDataToViewDto } from '@subscriptions/application/mappers';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for creating invoice data (datos fiscales).
 *
 * Flow: CreateInvoiceDataDto → domain entity → save via port → InvoiceDataViewDto
 *
 * @param port - Invoice data API port
 * @returns Use case executor
 */
export const createInvoiceDataUseCase = (port: IInvoiceDataApiPort) => {
  const execute = async (dto: CreateInvoiceDataDto): Promise<InvoiceDataViewDto> => {
    const rfcPrefix = dto.rfc.slice(0, 4);

    const span = tracer.startSpan('SUB.createInvoiceData', {
      attributes: { 'invoice.rfcPrefix': rfcPrefix },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const invoiceData = createInvoiceData({
          businessName: dto.businessName,
          firstName: dto.firstName,
          firstLastName: dto.firstLastName,
          secondLastName: dto.secondLastName,
          postalCode: dto.postalCode,
          rfc: dto.rfc,
          cfdi: dto.cfdi,
          email: dto.email,
          taxRegime: dto.taxRegime,
        });

        const saved = await port.create(invoiceData);
        const view = mapInvoiceDataToViewDto(saved);
        invoiceLog.info('createInvoiceData', {
          'invoice.uuid': view.uuid,
          'invoice.rfcPrefix': rfcPrefix,
        });
        return view;
      } catch (error) {
        invoiceLog.error('createInvoiceData', extractErrorAttributes(error));
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
